import { useRef, useState } from "react";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { BusinessDetails } from "@/features/business/components/BusinessDetails";
import { useBusinesses } from "@/features/business/hooks/businessQueries";
import { ProfileDetails } from "@/features/profile/components/ProfileDetails";
import { BusinessForms } from "@/features/forms/components/BusinessForms";
import { Button } from "@/shared/components/shadcn/Button";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";

export function DashboardPage() {
  const { signOut } = useAuth();
  const businesses = useBusinesses();
  const [signingOut, setSigningOut] = useState(false);
  const [error, setError] = useState("");
  const submitting = useRef(false);

  const handleSignOut = async () => {
    if (submitting.current) return;
    submitting.current = true;
    setSigningOut(true);
    setError("");
    try {
      await signOut();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Unable to sign out.");
    } finally {
      submitting.current = false;
      setSigningOut(false);
    }
  };

  return (
    <Page>
      <PageHeader title="Home" description="Froppio Forms" actions={
        <Button variant="outline" disabled={signingOut} onClick={handleSignOut}>
          {signingOut ? "Signing out..." : "Sign out"}
        </Button>
      } />
      <PageContent className="space-y-[26px] pt-3">
        {error && <ErrorState message={error} />}
        <Section title="Your forms">
          <p className="text-sm text-muted-foreground">Create and manage forms for each of your businesses.</p>
          {businesses.isLoading ? <LoadingSpinner message="Loading your businesses..." />
            : businesses.error ? <ErrorState message={businesses.error.message} onRetry={() => void businesses.refetch()} />
            : businesses.data?.length ? <div className="space-y-[26px]">{businesses.data.map(business =>
              <BusinessForms key={business.id} business={business} />)}</div>
            : <EmptyState message="No businesses are linked to this account." />}
        </Section>
        <ProfileDetails />
        <Section title="Business details">
          {!businesses.error && businesses.data?.map(business =>
            <BusinessDetails key={business.id} business={business} />)}
        </Section>
        <p className="text-sm text-muted-foreground">
          Manage your profile and businesses in the main Froppio app.
        </p>
      </PageContent>
    </Page>
  );
}
